import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/mtm/api')

/** 承运商状态：启用可被计划区段引用，停用只是档案停用，历史引用保留 */
export type CarrierStatus = 'active' | 'disabled'

/** 运输方式：与后端 internal/carrier/model.go 的 Modes 同源两份，改一边必须改另一边 */
export type TransportMode = 'truck' | 'rail' | 'ship' | 'air'

/** 承运商档案。字段与 services/mtm/internal/carrier/model.go 的 Carrier 一一对应。 */
export interface Carrier {
  id: number
  tenant_id: number
  code: string
  name: string
  /** 逗号分隔的运输方式，落库格式（"truck,rail"） */
  modes: string
  contact: string
  phone: string
  status: CarrierStatus
  created_at: string
  updated_at: string
}

/** 新建 / 编辑入参：modes 传逗号串（后端 ModesArg 也收数组，这里统一成落库格式）。 */
export interface CarrierInput {
  code: string
  name?: string
  modes: string
  contact?: string
  phone?: string
  status: CarrierStatus
}

export interface CarrierQuery extends PageQuery {
  keyword?: string
  status?: string
  mode?: string
}

/** 表单态：modes 是多选数组，空文本字段用 '' 表示没填 */
export interface CarrierForm {
  code: string
  name: string
  modes: TransportMode[]
  contact: string
  phone: string
  status: CarrierStatus
}

/** 运输方式字典，顺序与后端 Modes 一致，即前端下拉的展示顺序 */
export const MODES = ['truck', 'rail', 'ship', 'air'] as const

/** 与后端 services/mtm/internal/carrier/model.go 的 ModeNames 同源两份 */
export const MODE_LABELS: Record<TransportMode, string> = {
  truck: '公路',
  rail: '铁路',
  ship: '水运',
  air: '航空',
}

/** 与后端 services/mtm/internal/carrier/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<CarrierStatus, string> = {
  active: '启用',
  disabled: '停用',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<CarrierStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  active: 'success',
  disabled: 'info',
}

/** 落库的逗号串拆成运输方式列表：空段丢弃、按后端合法枚举过滤 */
export function parseModes(modes: string): TransportMode[] {
  return modes
    .split(',')
    .map((m) => m.trim())
    .filter((m): m is TransportMode => (MODES as readonly string[]).includes(m))
}

/**
 * 表单 → 请求体：空字段不进 body。
 * 承运商 id / 场站 id 这类数字字段在别处同理——undefined 在 JSON.stringify 里会被丢掉。
 */
export function carrierInput(form: CarrierForm): CarrierInput {
  const input: CarrierInput = {
    code: form.code.trim(),
    modes: form.modes.join(','),
    status: form.status,
  }
  const name = form.name.trim()
  if (name) input.name = name
  const contact = form.contact.trim()
  if (contact) input.contact = contact
  const phone = form.phone.trim()
  if (phone) input.phone = phone
  return input
}

export const listCarriers = (q: CarrierQuery): Promise<PageResult<Carrier>> =>
  api.get<PageResult<Carrier>>('/carriers', { ...q })

export const createCarrier = (input: CarrierInput): Promise<Carrier> =>
  api.post<Carrier>('/carriers', input)

export const updateCarrier = (id: number, input: CarrierInput): Promise<Carrier> =>
  api.put<Carrier>(`/carriers/${id}`, input)

export const deleteCarrier = (id: number): Promise<void> => api.del<void>(`/carriers/${id}`)

/** 下拉参照用的全量承运商：后端 size 上限 200 */
export const listCarrierOptions = (): Promise<Carrier[]> =>
  listCarriers({ page: 1, size: 200 }).then((page) => page.list)
